<template>
	<div>
		<!-- Header bar — flat text-link nav (Nuxt /pricing-formula style) -->
		<header style="background:white;border-bottom:1px solid #e2e8f0;">
			<div class="container-xl px-3 py-3 d-flex align-items-center justify-content-between gap-3">
				<div class="d-flex align-items-center gap-4 flex-wrap">
					<div class="d-flex align-items-center gap-2 flex-shrink-0">
						<span class="rounded d-inline-flex align-items-center justify-content-center fw-bold"
							style="width:36px;height:36px;background:#1d4ed8;color:white;font-size:14px;letter-spacing:0.05em;">CW</span>
						<h1 class="h6 fw-semibold mb-0" style="color:#0f172a;">Town Web's Clerkware pricing</h1>
					</div>
					<nav class="d-flex align-items-center flex-wrap" style="gap:18px;font-size:14px;">
						<router-link to="/cwpe/lookup" class="cwpe-nav-link"
							:class="{ active: $route.path.startsWith('/cwpe/lookup') || $route.path === '/cwpe' }">
							Lookup
						</router-link>
						<router-link to="/cwpe/pricing" class="cwpe-nav-link"
							:class="{ active: $route.path.startsWith('/cwpe/pricing') }">
							Competitive comparison
						</router-link>
						<router-link to="/cwpe/quotes" class="cwpe-nav-link"
							:class="{ active: $route.path.startsWith('/cwpe/quotes') }">
							Quote Builder
						</router-link>
						<router-link to="/cwpe/proposals" class="cwpe-nav-link"
							:class="{ active: $route.path.startsWith('/cwpe/proposals') }">
							Proposals
						</router-link>
						<router-link v-if="isCwpeAdmin" to="/cwpe/settings" class="cwpe-nav-link cwpe-nav-secondary"
							:class="{ active: $route.path.startsWith('/cwpe/settings') }">
							Pricing formula
						</router-link>
					</nav>
				</div>
				<span class="d-none d-md-inline" style="font-size:11px;color:#64748b;">HeyGov internal tool</span>
			</div>
		</header>

		<!-- Active municipality bar -->
		<div style="background:rgba(239,246,255,0.6);border-bottom:1px solid #dbeafe;">
			<div class="container-xl px-3 py-2 d-flex align-items-center gap-2 flex-wrap" style="font-size:12px;">
				<span class="fw-medium flex-shrink-0" style="color:#475569;">Active municipality:</span>

				<template v-if="activeMuni">
					<div class="d-inline-flex align-items-baseline gap-2 bg-white rounded px-2 py-1"
						style="border:1px solid #93c5fd;">
						<span class="fw-semibold" style="color:#0f172a;">{{ activeMuni.name || activeMuni.full_name }}</span>
						<span style="color:#94a3b8;">·</span>
						<span style="color:#475569;">{{ activeMuni.state }}</span>
						<span v-if="activeMuni.county" style="color:#94a3b8;font-weight:400;">· {{ activeMuni.county }}</span>
					</div>
					<button type="button" @click="refreshActiveMuni" :disabled="refreshing"
						class="btn btn-link btn-sm p-0 text-decoration-none d-inline-flex align-items-center"
						style="color:#1d4ed8;font-size:12px;"
						title="Re-fetch Census data & screenshot, then update the cached record">
						<span v-if="refreshing" class="spinner-border spinner-border-sm me-1" role="status"
							style="width:10px;height:10px;border-width:1.5px;"></span>
						{{ refreshing ? 'Refreshing…' : 'Refresh' }}
					</button>
					<button type="button" @click="clearActiveMuni" class="btn btn-link btn-sm p-0 text-decoration-none"
						style="color:#94a3b8;font-size:12px;">Clear</button>
				</template>

				<template v-else>
					<router-link to="/cwpe/lookup" class="cwpe-muni-link fst-italic text-decoration-none" style="color:#94a3b8 !important;">
						No municipality selected — go to Lookup to search
					</router-link>
				</template>

				<span class="ms-auto fst-italic d-none d-md-inline" style="font-size:10px;color:#64748b;">
					Stays selected across Lookup, Pricing engine, Quote Builder, and Proposals.
				</span>
			</div>
		</div>

		<router-view></router-view>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import restApi from '../../api/index.js'

const ACTIVE_KEY = 'cwpe_active_muni'

export default {
	name: 'CwpeParent',
	data() {
		return {
			activeMuni: null,
			refreshing: false,
		}
	},
	computed: {
		...mapState(['authUser']),
		isCwpeAdmin() {
			return !!this.authUser && this.authUser.role === 'Admin'
		},
	},
	created() {
		this.loadActiveMuni()
	},
	mounted() {
		// Two-way sync: anyone (Lookup form, Clear button, programmatic change)
		// who mutates `cwpe_active_muni` dispatches this window event so the
		// chip in this header bar refreshes without a route change.
		window.addEventListener('cwpe:active-muni-changed', this.loadActiveMuni)
	},
	beforeDestroy() {
		window.removeEventListener('cwpe:active-muni-changed', this.loadActiveMuni)
	},
	watch: {
		'$route'() {
			this.loadActiveMuni()
		},
	},
	methods: {
		loadActiveMuni() {
			const raw = localStorage.getItem(ACTIVE_KEY)
			this.activeMuni = raw ? JSON.parse(raw) : null
		},
		clearActiveMuni() {
			localStorage.removeItem(ACTIVE_KEY)
			this.activeMuni = null
			window.dispatchEvent(new CustomEvent('cwpe:active-muni-changed'))
			// Send the rep back to Lookup so they land on the empty-state hero
			// instead of staring at a stripped Pricing / Quote Builder page.
			if (!this.$route.path.startsWith('/cwpe/lookup')) {
				this.$router.push('/cwpe/lookup')
			}
		},
		// Force a fresh pull (Census population/trends/SNAP + homepage
		// screenshot) and overwrite the cached cwpe_municipality_data row.
		// The cache itself never expires, so this is the only way to refresh it.
		async refreshActiveMuni() {
			if (!this.activeMuni || !this.activeMuni.id || this.refreshing) return
			this.refreshing = true
			// Put any open CWPE page into its loading state right away — before
			// the (potentially slow) force-refresh + screenshot — so the rep sees
			// the skeleton/spinner for the whole duration, not just a flash at the end.
			window.dispatchEvent(new CustomEvent('cwpe:muni-refreshing'))
			try {
				const { data } = await restApi.post('/cwpe/pricing/lookup', {
					municipality_id: this.activeMuni.id,
					name:            this.activeMuni.full_name || this.activeMuni.name,
					state:           this.activeMuni.state,
					force_refresh:   true,
				})
				// Re-capture the homepage screenshot too. Best-effort — a
				// missing/invalid URL or worker hiccup must not fail the refresh.
				let url = this.activeMuni.website || data?.municipality_data?.website_url || null
				if (url && !/^https?:\/\//i.test(url)) url = 'https://' + url
				if (url) {
					try {
						await restApi.post('/cwpe/pricing/capture-screenshot', {
							municipality_id: this.activeMuni.id,
							url,
						})
					} catch (_) { /* screenshot is best-effort */ }
				}
				this.$swal.fire({
					toast: true, position: 'top-end', icon: 'success',
					title: 'Data refreshed', showConfirmButton: false, timer: 1600,
				})
			} catch (err) {
				this.$swal.fire({
					icon: 'error', title: 'Refresh failed',
					text: err?.response?.data?.error || err?.message || '',
				})
			} finally {
				this.refreshing = false
				// Reload the open page from the now-fresh cache (and clear its
				// loading state). Fires on success and failure alike so the page
				// never gets stuck in the skeleton.
				window.dispatchEvent(new CustomEvent('cwpe:muni-refreshed'))
			}
		},
	},
}
</script>

<style scoped>
.cwpe-nav-link {
	color: #475569;
	text-decoration: none;
	padding: 4px 0;
	border-bottom: 2px solid transparent;
	transition: color .12s ease, border-color .12s ease;
	background-color: transparent !important;
}
.cwpe-nav-link:hover {
	color: #1d4ed8;
}
/* Override the global .router-link-exact-active.router-link-active rule
   in _juniper.scss that paints active links solid blue. */
.cwpe-nav-link.active,
.cwpe-nav-link.router-link-active,
.cwpe-nav-link.router-link-exact-active {
	color: #1d4ed8 !important;
	font-weight: 500;
	border-bottom-color: #1d4ed8;
	background-color: transparent !important;
}
.cwpe-nav-secondary {
	color: #64748b;
	font-size: 12px;
	padding-left: 14px;
	margin-left: 6px;
	border-left: 1px solid #e2e8f0;
}
.cwpe-nav-secondary.active {
	color: #1d4ed8 !important;
}
/* Same override for the muni-bar router-links */
.cwpe-muni-link,
.cwpe-muni-link.router-link-active,
.cwpe-muni-link.router-link-exact-active {
	background-color: transparent !important;
	color: inherit;
}
</style>
